import React, { useState } from 'react';
import { toast } from 'sonner';
import { Smartphone, Mail, Loader2, ShieldCheck, ShieldOff, RefreshCw } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { RecoveryCodes } from './RecoveryCodes';
import { ManageDialog } from './ManageDialog';

export const errMsg = (e, d = 'Ocurrió un error') => (typeof e.response?.data?.detail === 'string' ? e.response.data.detail : d);

const METHODS = [
  { id: 'totp', icon: Smartphone, title: 'App autenticadora', desc: 'Google Authenticator, Microsoft Authenticator, 1Password…' },
  { id: 'email', icon: Mail, title: 'Código por correo', desc: 'Te enviamos un código de 6 dígitos cada vez que ingresas' },
];

export const MfaSetup = ({ status, onChange }) => {
  const { api, refreshUser } = useAuth();
  const [pending, setPending] = useState(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [codes, setCodes] = useState(null);
  const [manage, setManage] = useState(null);

  const start = async (method) => {
    setBusy(true);
    try {
      const { data } = await api.post('/security/2fa/setup', { method });
      setPending(data);
      setCode('');
      if (method === 'email') toast.success(`Código enviado a ${data.email_hint}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/security/2fa/enable', { code: code.trim() });
      setCodes(data.recovery_codes);
      setPending(null);
      toast.success('Doble factor activado');
      onChange(); refreshUser();
    } catch (e) { toast.error(errMsg(e, 'Código incorrecto')); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="mfa-setup">
      <div className="flex items-center gap-3">
        {status?.enabled ? <ShieldCheck className="w-6 h-6 text-emerald-400" /> : <ShieldOff className="w-6 h-6 text-amber-400" />}
        <div>
          <p className="font-semibold" data-testid="mfa-status-text">{status?.enabled ? `Activo · ${status.method === 'totp' ? 'App autenticadora' : 'Código por correo'}` : 'Doble factor desactivado'}</p>
          <p className="text-xs text-slate-400">{status?.enabled ? `${status.recovery_codes_left} códigos de recuperación disponibles` : status?.required ? 'Obligatorio para tu cuenta de administrador' : 'Recomendado para proteger los datos de tus clientes'}</p>
        </div>
      </div>
      {codes && <RecoveryCodes codes={codes} onClose={() => setCodes(null)} />}
      {!pending && (
        <div className="grid sm:grid-cols-2 gap-3">
          {METHODS.map((m) => (
            <button key={m.id} disabled={busy} onClick={() => start(m.id)} data-testid={`mfa-method-${m.id}-btn`}
              className="text-left p-4 rounded-xl border border-slate-700 hover:border-emerald-500/60 hover:bg-emerald-500/5 transition-colors">
              <m.icon className="w-5 h-5 text-emerald-400 mb-2" />
              <p className="font-medium text-sm">{status?.enabled && status.method === m.id ? `Reconfigurar ${m.title.toLowerCase()}` : status?.enabled ? `Cambiar a ${m.title.toLowerCase()}` : m.title}</p>
              <p className="text-xs text-slate-400 mt-1">{m.desc}</p>
            </button>
          ))}
        </div>
      )}
      {pending && (
        <div className="p-4 rounded-xl border border-emerald-500/30 bg-slate-900/40 space-y-3" data-testid="mfa-pending-panel">
          {pending.method === 'totp' ? (
            <div className="flex flex-col sm:flex-row gap-4 items-start">
              <img src={pending.qr} alt="Código QR" className="w-40 h-40 bg-white p-2 rounded-lg" data-testid="mfa-qr-img" />
              <div className="text-sm text-slate-300 space-y-2">
                <p>1. Escanea el código QR con tu app autenticadora.</p>
                <p>2. ¿No puedes escanear? Ingresa esta clave manualmente:</p>
                <code className="block break-all text-xs bg-slate-800 p-2 rounded font-mono" data-testid="mfa-secret-text">{pending.secret}</code>
                <p>3. Escribe el código de 6 dígitos que aparece.</p>
              </div>
            </div>
          ) : <p className="text-sm text-slate-300">Escribe el código que enviamos a <b>{pending.email_hint}</b>.</p>}
          <div className="flex gap-2">
            <input value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} inputMode="numeric" placeholder="000000" data-testid="mfa-setup-code-input"
              className="flex-1 px-3 py-2 bg-slate-900/60 border border-slate-700 rounded-lg font-mono tracking-[0.3em]" />
            <button onClick={confirm} disabled={busy || code.trim().length !== 6} data-testid="mfa-setup-confirm-btn" className="btn-primary px-4 flex items-center gap-2 disabled:opacity-50">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Activar
            </button>
            <button onClick={() => setPending(null)} data-testid="mfa-setup-cancel-btn" className="px-3 text-slate-400 hover:text-white text-sm">Cancelar</button>
          </div>
        </div>
      )}
      {status?.enabled && !pending && (
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setManage('recovery')} data-testid="mfa-regenerate-btn" className="text-sm px-3 py-2 rounded-lg border border-slate-700 hover:bg-slate-800 flex items-center gap-2">
            <RefreshCw className="w-4 h-4" /> Nuevos códigos de recuperación
          </button>
          {!status.required && (
            <button onClick={() => setManage('disable')} data-testid="mfa-disable-btn" className="text-sm px-3 py-2 rounded-lg border border-rose-500/40 text-rose-300 hover:bg-rose-500/10">
              Desactivar doble factor
            </button>
          )}
        </div>
      )}
      {manage && <ManageDialog mode={manage} method={status?.method} onClose={() => setManage(null)}
        onDone={(data) => { setManage(null); if (data?.recovery_codes) setCodes(data.recovery_codes); onChange(); refreshUser(); }} />}
    </div>
  );
};
